/**
 * ADLSectionCard.jsx
 * CCH Care — Phase 3 → Phase 5d
 * Last Modified: 2026-05-19
 *
 * Wrapper card for each ADL section.
 * - Collapsible after signing (auto-collapses on sign, can manually expand)
 * - Shows summary line when collapsed
 * - Save indicator, status badge, Sign/Amend footer
 *
 * Amend button restyle (2026-05-19): both Amend buttons (expanded and
 * collapsed signed footers) now use the amber pill style — amber text,
 * amber border, surface fill, rounded pill. This matches the per-meal
 * Amend button in MealsForm and the amber "AMENDED" badge: amber is the
 * app-wide amendment semantic, so every Amend affordance now reads the
 * same. Previously these were a muted grey rectangle, inconsistent with
 * the rest of the amendment UI.
 *
 * Attested-section Amend gate (2026-05-19): the Amend button is hidden
 * when `attested` is true. A section signed via the "Nothing to Report"
 * attestation has no charted rows, so the amend modal would open to
 * "No entries found" — a dead end. Correcting a wrong attestation is a
 * retract-and-re-document flow (surfaced by ADLSectionNA), not the amend
 * flow. Forms pass attested={!!attestation}.
 *
 * Late Documentation Phase 3 (2026-05-16): the Amend button is no longer
 * gated on !disabled. It renders inside the isSigned block, but signed
 * sections are always passed disabled=true (the form-body lock), so the
 * !disabled gate made the Amend affordance permanently unreachable. The
 * Amend button now shows whenever a section is signed; `disabled`
 * continues to lock the form body only.
 *
 * Phase 5d: added 'na' status — section marked N/A in care plan.
 * N/A sections render collapsed with an N/A badge, no sign button,
 * no amend button. Body is still rendered (shows the N/A banner from
 * ADLSectionNA) but the card starts collapsed and can be expanded.
 *
 * Dark mode: replaced hardcoded '#F1F5F9' / '#F8FAFC' NA backgrounds
 *            with COLORS.bg so they respond to theme switching.
 */

import { useState, useEffect } from 'react';
import { COLORS } from '../../config/constants';

const STATUS_CONFIG = {
  draft:  { label: 'In Progress', color: COLORS.warning,   bg: COLORS.warningLight },
  signed: { label: 'Signed',      color: COLORS.accent,    bg: COLORS.accentLight  },
  na:     { label: 'N/A',         color: COLORS.textMuted, bg: COLORS.bg           },
};

export default function ADLSectionCard({
  title,
  icon,
  sectionKey,
  status = 'draft',
  saveStatus = 'idle',
  hasAmendments = false,
  summary = null,
  onSign,
  disabled = false,
  attested = false,
  children,
}) {
  const cfg      = STATUS_CONFIG[status] ?? STATUS_CONFIG.draft;
  const isSigned = status === 'signed';
  const isNA     = status === 'na';

  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    if (isSigned || isNA) setCollapsed(true);
    else setCollapsed(false);
  }, [isSigned, isNA]);

  const borderColor     = isSigned ? COLORS.accentAlpha : COLORS.border;
  const headerBg        = isSigned ? COLORS.accentLight : isNA ? COLORS.bg : COLORS.bg;
  const headerClickable = isSigned || isNA;

  function handleHeaderKey(e) {
    if (headerClickable && (e.key === 'Enter' || e.key === ' ')) {
      setCollapsed(c => !c);
    }
  }

  return (
    <div style={{
      background: COLORS.surface,
      border: `1px solid ${borderColor}`,
      borderRadius: 12,
      marginBottom: 12,
      overflow: 'hidden',
      opacity: disabled && !isSigned && !isNA ? 0.7 : 1,
      transition: 'border-color 0.2s',
    }}>

      {/* Section header */}
      <div
        role={headerClickable ? 'button' : undefined}
        tabIndex={headerClickable ? 0 : undefined}
        onClick={() => headerClickable && setCollapsed(c => !c)}
        onKeyDown={handleHeaderKey}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 16px',
          background: headerBg,
          borderBottom: collapsed ? 'none' : `1px solid ${COLORS.border}`,
          cursor: headerClickable ? 'pointer' : 'default',
          userSelect: 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 20, opacity: isNA ? 0.5 : 1 }}>{icon}</span>
          <div>
            <span style={{
              fontSize: 15, fontWeight: 700,
              color: isNA ? COLORS.textMuted : COLORS.text,
            }}>
              {title}
            </span>
            {(isSigned || isNA) && collapsed && summary && (
              <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 1 }}>
                {summary}
              </div>
            )}
            {isNA && collapsed && (
              <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 1 }}>
                Not applicable for this resident
              </div>
            )}
          </div>
          {hasAmendments && (
            <span style={{
              fontSize: 10, fontWeight: 700, color: COLORS.warning,
              background: COLORS.warningLight, padding: '2px 6px', borderRadius: 4,
            }}>
              AMENDED
            </span>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {!isSigned && !isNA && saveStatus === 'saving' && (
            <span style={{ fontSize: 11, color: COLORS.textMuted }}>Saving…</span>
          )}
          {!isSigned && !isNA && saveStatus === 'saved' && (
            <span style={{ fontSize: 11, color: COLORS.accent, fontWeight: 600 }}>✓ Saved</span>
          )}
          {!isSigned && !isNA && saveStatus === 'error' && (
            <span style={{ fontSize: 11, color: COLORS.danger, fontWeight: 600 }}>⚠ Save failed</span>
          )}

          <span style={{
            fontSize: 11, fontWeight: 700,
            color: cfg.color, background: cfg.bg,
            padding: '3px 8px', borderRadius: 20,
            border: isNA ? `1px solid ${COLORS.border}` : 'none',
          }}>
            {isSigned ? '✓ ' : ''}{cfg.label}
          </span>

          {headerClickable && (
            <span style={{
              fontSize: 12, color: COLORS.textMuted,
              transform: collapsed ? 'rotate(0deg)' : 'rotate(180deg)',
              transition: 'transform 0.2s',
              display: 'inline-block',
            }}>
              ▼
            </span>
          )}
        </div>
      </div>

      {/* Collapsible body */}
      {!collapsed && (
        <>
          <div style={{ padding: '14px 16px' }}>
            {children}
          </div>

          {!isSigned && !isNA && !disabled && onSign && (
            <div style={{
              padding: '12px 16px',
              borderTop: `1px solid ${COLORS.border}`,
              background: COLORS.bg,
              display: 'flex', justifyContent: 'flex-end',
            }}>
              <button
                onClick={() => onSign(sectionKey)}
                style={{
                  padding: '10px 24px',
                  background: COLORS.primary, color: '#fff',
                  border: 'none', borderRadius: 8,
                  fontSize: 13, fontWeight: 700, cursor: 'pointer',
                }}
              >
                Sign Section ✍️
              </button>
            </div>
          )}

          {isSigned && (
            <div style={{
              padding: '10px 16px',
              borderTop: `1px solid ${COLORS.border}`,
              background: COLORS.accentLight,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <span style={{ fontSize: 12, color: COLORS.accent, fontWeight: 600 }}>
                ✓ Section signed
              </span>
              {/* Amendment is the sanctioned correction path for a signed
                  section, so the Amend button is NOT gated on `disabled`
                  (a signed section is always disabled — that lock is for
                  the form body, not the amend affordance). Amber pill —
                  consistent with the per-meal Amend button in MealsForm
                  and the amber AMENDED badge; amendment is the app-wide
                  amber semantic.
                  It IS gated on !attested: a section signed via the
                  "Nothing to Report" attestation has no charted rows, so
                  there is nothing for the amend modal to amend (it would
                  open to "No entries found"). Correcting a wrong
                  attestation is a retract-and-re-document flow, surfaced
                  by ADLSectionNA — not the amend flow. */}
              {!attested && (
                <button
                  onClick={() => onSign?.('amend_' + sectionKey)}
                  style={{
                    background: COLORS.surface,
                    border: `1px solid ${COLORS.warning}`,
                    borderRadius: 20, padding: '4px 14px',
                    fontSize: 11, fontWeight: 700,
                    color: COLORS.warning, cursor: 'pointer',
                  }}
                >
                  Amend
                </button>
              )}
            </div>
          )}

          {isNA && (
            <div style={{
              padding: '10px 16px',
              borderTop: `1px solid ${COLORS.border}`,
              background: COLORS.bg,
              display: 'flex', alignItems: 'center',
            }}>
              <span style={{ fontSize: 12, color: COLORS.textMuted }}>
                🚫 Not required — contact a supervisor to change
              </span>
            </div>
          )}
        </>
      )}

      {/* Collapsed signed footer */}
      {collapsed && isSigned && (
        <div style={{
          padding: '8px 16px',
          background: COLORS.accentLight,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <span style={{ fontSize: 12, color: COLORS.accent, fontWeight: 600 }}>
            ✓ Signed
          </span>
          {/* See note above — Amend is intentionally not gated on
              `disabled`, uses the amber pill style for consistency, and
              is hidden on an attested ("Nothing to Report") section. */}
          {!attested && (
            <button
              onClick={e => { e.stopPropagation(); onSign?.('amend_' + sectionKey); }}
              style={{
                background: COLORS.surface,
                border: `1px solid ${COLORS.warning}`,
                borderRadius: 20, padding: '4px 14px',
                fontSize: 11, fontWeight: 700,
                color: COLORS.warning, cursor: 'pointer',
              }}
            >
              Amend
            </button>
          )}
        </div>
      )}

      {/* Collapsed N/A footer */}
      {collapsed && isNA && (
        <div style={{
          padding: '8px 16px',
          background: COLORS.bg,
          display: 'flex', alignItems: 'center',
        }}>
          <span style={{ fontSize: 12, color: COLORS.textMuted }}>
            🚫 Not applicable for this resident
          </span>
        </div>
      )}
    </div>
  );
}